import React, { useCallback, useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useNavigate, useParams } from 'react-router-dom';
import { Axios } from 'services/axios';
import { Loading, Notify } from 'notiflix';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';

// Formats the toolbar can actually produce — anything else arriving via
// copy/paste (from Word, Google Docs, another site, etc.) is stripped down
// to this set instead of carrying over stray inline styles/classes that
// would just get sanitized away anyway.
const ALLOWED_PASTE_FORMATS = [
  'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'indent', 'header', 'color', 'background', 'link',
];

const stripUnknownPasteFormats = (node, delta) => {
  delta.ops.forEach((op) => {
    if (!op.attributes) return;
    Object.keys(op.attributes).forEach((key) => {
      if (!ALLOWED_PASTE_FORMATS.includes(key)) delete op.attributes[key];
    });
  });
  return delta;
};

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ color: [] }, { background: [] }],
    ['link'],
    ['clean'],
  ],
  clipboard: {
    matchVisual: false,
    matchers: [[Node.ELEMENT_NODE, stripUnknownPasteFormats]],
  },
};

const toSlug = (str) =>
  str?.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || '';

const LegalPageForm = () => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(pageId);

  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [pageContent, setPageContent] = useState('');
  const [pageInShort, setPageInShort] = useState('');
  const [pageActive, setPageActive] = useState(true);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState('');

  const { control, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues: {
      categoryId: '',
      countryId: '',
      title: '',
      slug: '',
      description: '',
      readTime: '',
      lastUpdated: '',
      order: 0,
    },
  });

  const fetchMeta = useCallback(async () => {
    try {
      const [catRes, countryRes] = await Promise.all([
        Axios.fetch('/legal/categories'),
        Axios.fetch('/public/countries/active'),
      ]);
      setCategories(catRes.data?.data || []);
      setCountries(countryRes.data?.data?.countries || []);
    } catch {
      Notify.failure('Failed to load form data');
    }
  }, []);

  const fetchPage = useCallback(async () => {
    Loading.circle('Loading page…');
    try {
      const res = await Axios.fetch(`/legal/pages/${pageId}`);
      const p = res.data?.data;
      reset({
        categoryId: p.categoryId || '',
        countryId: p.countryId || '',
        title: p.title || '',
        slug: p.slug || '',
        description: p.description || '',
        readTime: p.readTime || '',
        lastUpdated: p.lastUpdated ? p.lastUpdated.split('T')[0] : '',
        order: p.order ?? 0,
      });
      setPageContent(p.content || '');
      setPageInShort(p.inShort || '');
      setPageActive(p.active !== false);
      setIconPreview(p.icon || '');
    } catch {
      Notify.failure('Failed to load page');
    } finally {
      Loading.remove();
    }
  }, [pageId, reset]);

  useEffect(() => {
    fetchMeta();
    if (isEdit) fetchPage();
  }, [fetchMeta, fetchPage, isEdit]);

  const onSubmit = async (data) => {
    Loading.circle('Saving…');
    const formData = new FormData();
    formData.append('categoryId', data.categoryId || '');
    formData.append('countryId', data.countryId || '');
    formData.append('title', data.title || '');
    formData.append('slug', data.slug || '');
    formData.append('description', data.description || '');
    formData.append('readTime', data.readTime || '');
    formData.append('lastUpdated', data.lastUpdated || '');
    formData.append('order', data.order ?? 0);
    formData.append('active', String(pageActive));
    formData.append('content', pageContent);
    formData.append('inShort', pageInShort);
    if (iconFile) formData.append('icon', iconFile);

    const url = isEdit ? `/legal/pages?pageId=${pageId}` : '/legal/pages';
    try {
      await Axios.post(url, formData, { 'Content-Type': 'multipart/form-data' });
      Notify.success(isEdit ? 'Page updated' : 'Page created');
      navigate('/legal-pages?tab=1');
    } catch (err) {
      Notify.failure(err.response?.data?.message || 'Save failed');
    } finally {
      Loading.remove();
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* Header */}
      <MDBox
        mt={4}
        mb={3}
        px={3}
        py={2.5}
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(102,126,234,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <MDBox>
          <MDTypography variant="h4" fontWeight="bold" color="white" mb={0.5}>
            {isEdit ? '✏️ Edit Legal Page' : '➕ Add Legal Page'}
          </MDTypography>
          <MDTypography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            {isEdit ? 'Update the content and settings for this page' : 'Fill in the details to create a new legal page'}
          </MDTypography>
        </MDBox>
        <MDButton
          variant="outlined"
          onClick={() => navigate('/legal-pages')}
          sx={{
            color: '#fff',
            borderColor: 'rgba(255,255,255,0.5)',
            '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.1)' },
          }}
        >
          ← Back to Legal Pages
        </MDButton>
      </MDBox>

      {/* Form Card */}
      <MDBox
        sx={{
          background: '#fff',
          borderRadius: '16px',
          boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
          p: 3,
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2.5}>

            {/* Basic Info */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold">Basic Info</Typography>
              <Divider />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="categoryId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel id="cat-label" shrink>Category</InputLabel>
                    <Select {...field} labelId="cat-label" label="Category" displayEmpty notched>
                      <MenuItem value=""><em>None</em></MenuItem>
                      {categories.map((c) => (
                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="countryId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel id="country-label" shrink>Country (optional)</InputLabel>
                    <Select {...field} labelId="country-label" label="Country (optional)" displayEmpty notched>
                      <MenuItem value=""><em>All Countries</em></MenuItem>
                      {countries.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.flagEmoji ? `${c.flagEmoji} ` : ''}{c.countryName} ({c.countryCode})
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, px: 1.5 }}>
                      Leave blank to show for all countries
                    </Typography>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="title"
                control={control}
                rules={{ required: 'Title is required' }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Page Title *"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    onChange={(e) => {
                      field.onChange(e);
                      if (!isEdit) setValue('slug', toSlug(e.target.value));
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="slug"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Slug (URL)"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText={`URL: /legal/${field.value || '...'}`}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Short Description (shown on card)"
                    fullWidth
                    multiline
                    rows={2}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            {/* Meta */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold" mt={1}>Meta / Header Fields</Typography>
              <Divider />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Controller
                name="readTime"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Read Time" fullWidth InputLabelProps={{ shrink: true }} helperText='e.g. "8"' />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Controller
                name="order"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Display Order"
                    type="number"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText="Lower number = appears first"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Controller
                name="lastUpdated"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Updated Date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText="Shown as 'Last updated' on the page"
                  />
                )}
              />
            </Grid>

            {/* Active toggle */}
            <Grid item xs={12} sm={6}>
              <MDBox
                display="flex"
                alignItems="center"
                gap={1.5}
                sx={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  px: 2,
                  py: 1.5,
                  background: pageActive ? 'rgba(76,175,80,0.05)' : 'rgba(244,67,54,0.05)',
                  transition: 'background 0.2s',
                }}
              >
                <Switch
                  checked={pageActive}
                  onChange={(e) => setPageActive(e.target.checked)}
                  color="success"
                />
                <MDBox>
                  <MDTypography variant="button" fontWeight="medium">
                    {pageActive ? '✅ Active' : '❌ Inactive'}
                  </MDTypography>
                  <MDTypography variant="caption" color="text" display="block">
                    {pageActive ? 'Page is visible on the website' : 'Page is hidden from the website'}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Grid>

            {/* Icon */}
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>
                Icon Image {isEdit && iconPreview ? '(upload new to replace)' : ''}
              </Typography>
              {iconPreview && (
                <Box mb={1}>
                  <img
                    src={iconPreview}
                    alt="icon"
                    style={{ height: 48, width: 48, objectFit: 'contain', border: '1px solid #e2e8f0', borderRadius: 8, padding: 4 }}
                  />
                </Box>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setIconFile(file);
                    setIconPreview(URL.createObjectURL(file));
                  }
                }}
                style={{ display: 'block' }}
              />
            </Grid>

            {/* In Short */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold" mt={1}>
                &quot;In Short&quot; Summary Box
              </Typography>
              <Divider />
              <Typography variant="caption" color="textSecondary">
                A brief highlight box shown at the top of the policy page.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="In Short Text"
                fullWidth
                multiline
                rows={3}
                InputLabelProps={{ shrink: true }}
                value={pageInShort}
                onChange={(e) => setPageInShort(e.target.value)}
              />
            </Grid>

            {/* Full Content */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold" mt={1}>Full Page Content</Typography>
              <Divider />
              <Typography variant="caption" color="textSecondary">
                Complete HTML content of the policy page. Use headings, lists, and paragraphs.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <ReactQuill
                theme="snow"
                modules={quillModules}
                value={pageContent}
                onChange={setPageContent}
                style={{ height: 400, marginBottom: 60 }}
              />
            </Grid>

          </Grid>

          {/* Actions */}
          <MDBox display="flex" justifyContent="flex-end" gap={2} mt={2}>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={() => navigate('/legal-pages')}
            >
              Cancel
            </MDButton>
            <MDButton
              type="submit"
              variant="gradient"
              color="info"
              sx={{ px: 4 }}
            >
              {isEdit ? '💾 Update Page' : '✅ Create Page'}
            </MDButton>
          </MDBox>
        </form>
      </MDBox>
    </DashboardLayout>
  );
};

export default LegalPageForm;
